// extra-fx-print — print/halftone effects
// CMYK halftone, Risograph, Newspaper print

const N = (v) => Number(v) || 0;

// ---- CMYK halftone ----
const cmykHalftone = async (canvas, ctx, dotSize, angle) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    const ds = Math.max(2, Math.floor(N(dotSize)));
    const baseAngle = N(angle) * Math.PI / 180;
    const angles = [baseAngle, baseAngle + Math.PI/6, baseAngle + Math.PI/3, baseAngle + Math.PI/2];
    const channels = ['C', 'M', 'Y', 'K'];
    const inkColors = [[0, 255, 255], [255, 0, 255], [255, 255, 0], [0, 0, 0]];
    // Fill white
    const out = new Uint8ClampedArray(data.length);
    for (let i = 0; i < out.length; i += 4) { out[i] = 255; out[i+1] = 255; out[i+2] = 255; out[i+3] = 255; }
    for (let ch = 0; ch < 4; ch++) {
        const ang = angles[ch];
        const cosA = Math.cos(ang), sinA = Math.sin(ang);
        const [inkR, inkG, inkB] = inkColors[ch];
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                const i = (y * w + x) * 4;
                const r = data[i], g = data[i+1], b = data[i+2];
                let val;
                if (ch === 0) val = 1 - r / 255;      // C
                else if (ch === 1) val = 1 - g / 255;  // M
                else if (ch === 2) val = 1 - b / 255;  // Y
                else val = 1 - Math.min(r, g, b) / 255; // K
                if (val <= 0.01) continue;
                // Rotate coordinates
                const rx = x * cosA + y * sinA;
                const ry = -x * sinA + y * cosA;
                // Grid position
                const gx = Math.floor(rx / ds), gy = Math.floor(ry / ds);
                const cxp = (gx + 0.5) * ds, cyp = (gy + 0.5) * ds;
                // Inverse rotate to get dot center in image space
                const dotX = cxp * cosA - cyp * sinA;
                const dotY = cxp * sinA + cyp * cosA;
                const dist = Math.sqrt((x - dotX)**2 + (y - dotY)**2);
                const dotR = val * ds * 0.5;
                if (dist <= dotR) {
                    out[i] = Math.min(out[i], 255 - inkR * val);
                    out[i+1] = Math.min(out[i+1], 255 - inkG * val);
                    out[i+2] = Math.min(out[i+2], 255 - inkB * val);
                }
            }
        }
    }
    ctx.putImageData(new ImageData(out, w, h), 0, 0);
};

// ---- Risograph ----
const risograph = async (canvas, ctx, hue1, hue2, offset) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    const h1 = N(hue1), h2 = N(hue2);
    const off = Math.max(0, Math.floor(N(offset)));
    const [r1, g1, b1] = hslToRgb(h1 / 360, 0.8, 0.5);
    const [r2, g2, b2] = hslToRgb(h2 / 360, 0.8, 0.5);
    const out = new Uint8ClampedArray(data.length);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const i = (y * w + x) * 4;
        const lum = (0.299*data[i] + 0.587*data[i+1] + 0.114*data[i+2]) / 255;
        // Posterize to 4 levels
        const level = Math.floor(lum * 4) / 4;
        // Layer 1 (shifted)
        const x1 = Math.max(0, Math.min(w-1, x - off));
        const i1 = (y * w + x1) * 4;
        const lum1 = (0.299*data[i1] + 0.587*data[i1+1] + 0.114*data[i1+2]) / 255;
        const level1 = Math.floor(lum1 * 4) / 4;
        // Dot texture
        const dot = ((x + y) % 3 === 0) ? 0.85 : 1;
        out[i] = 255 - (1 - level1) * r1 * dot - (1 - level) * r2 * dot;
        out[i+1] = 255 - (1 - level1) * g1 * dot - (1 - level) * g2 * dot;
        out[i+2] = 255 - (1 - level1) * b1 * dot - (1 - level) * b2 * dot;
        out[i+3] = 255;
    }
    ctx.putImageData(new ImageData(out, w, h), 0, 0);
};
function hslToRgb(h, s, l) {
    let r, g, b;
    if (s === 0) { r = g = b = l; }
    else {
        const hue2rgb = (p, q, t) => { if (t < 0) t += 1; if (t > 1) t -= 1; if (t < 1/6) return p + (q - p) * 6 * t; if (t < 1/2) return q; if (t < 2/3) return p + (q - p) * (2/3 - t) * 6; return p; };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
        r = hue2rgb(p, q, h + 1/3); g = hue2rgb(p, q, h); b = hue2rgb(p, q, h - 1/3);
    }
    return [r * 255, g * 255, b * 255];
}

// ---- Newspaper print ----
const newspaper = async (canvas, ctx, dotSize, contrast) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    const ds = Math.max(2, Math.floor(N(dotSize)));
    const con = N(contrast) / 100;
    const out = new Uint8ClampedArray(data.length);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const i = (y * w + x) * 4;
        let lum = (0.299*data[i] + 0.587*data[i+1] + 0.114*data[i+2]) / 255;
        // Contrast
        lum = Math.max(0, Math.min(1, (lum - 0.5) * (1 + con) + 0.5));
        // Halftone dot
        const gx = Math.floor(x / ds), gy = Math.floor(y / ds);
        const cxp = (gx + 0.5) * ds, cyp = (gy + 0.5) * ds;
        const dist = Math.sqrt((x - cxp)**2 + (y - cyp)**2);
        const dotR = (1 - lum) * ds * 0.6;
        const isDot = dist <= dotR;
        // Paper texture (noise)
        const noise = (Math.random() - 0.5) * 10;
        const v = isDot ? Math.max(0, 30 + noise) : Math.min(255, 240 + noise);
        out[i] = v; out[i+1] = v; out[i+2] = v; out[i+3] = 255;
    }
    ctx.putImageData(new ImageData(out, w, h), 0, 0);
};

const descriptors = [
    { name: 'print-cmyk-halftone', inputs: [
        { name: 'Dot Size', type: 'knob', min: 2, max: 20, step: 1, value: 6 },
        { name: 'Angle', type: 'knob', min: 0, max: 90, step: 1, value: 15 }], fn: cmykHalftone },
    { name: 'print-risograph', inputs: [
        { name: 'Hue 1', type: 'knob', min: 0, max: 360, step: 1, value: 350 },
        { name: 'Hue 2', type: 'knob', min: 0, max: 360, step: 1, value: 200 },
        { name: 'Offset', type: 'knob', min: 0, max: 20, step: 1, value: 3 }], fn: risograph },
    { name: 'print-newspaper', inputs: [
        { name: 'Dot Size', type: 'knob', min: 2, max: 15, step: 1, value: 4 },
        { name: 'Contrast', type: 'knob', min: 0, max: 100, step: 1, value: 30 }], fn: newspaper },
];

const lib = {};
descriptors.forEach(d => lib[d.name] = d.fn);

module.exports = { descriptors, lib };
